import { type JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { ArchiveItemJson, ArchiveView } from '../../summary/archive-view.js';

import { escapeForRegExp } from '@rauschma/helpers/lang/regexp.js';
import { removePrefixMaybe } from '../../util/string-tools.js';
import { iterFilter, iterFlatMap, toEntries } from '../../util/sync-iter-tools.js';
import { SiteLink } from '../../view/site-link.js';
import { pageCoreContent } from '../site-shell/simple-site-shell-css-constants.js';
import { PageNavigation, type NavigationPageHandling } from '../util/page-navigation.js';
import './simple-archive-view.css';

const fieldKeyToDisplayStringPairs = [
  ['year', 'Year'],
  ['month', 'Month'],
  ['tags', 'Tag'],
  ['pageIndex', 'Page'],
  ['filterText', 'Text'],
] as const;

type FieldKey = ((typeof fieldKeyToDisplayStringPairs)[number])[0];
const fieldKeyToDisplayString = new Map<string, string>(fieldKeyToDisplayStringPairs);
const displayStringToFieldKey = new Map<string, string>(
  fieldKeyToDisplayStringPairs
  .map(([key, display]) => [display, key])
);

//========== Views: archive items

export const HYDRATION_ROOT = 'hydration-root';
export const SimpleArchiveView: ArchiveView =
  (props) => {
    return (
      <div id={HYDRATION_ROOT}>
        <ArchiveViewCore {...props} />
      </div>
    );
  };

interface ArchiveViewCoreProps {
  pageHeadingHtml: string;
  itemsPerPage: number;
}
export function ArchiveViewCore({pageHeadingHtml, itemsPerPage}: ArchiveViewCoreProps) {
  const [archiveItems, setArchiveItems] = useState<null | Array<ArchiveItem>>(null);
  useEffect((async () => {
    const jsonFileName = location.pathname.replace(/\.html$/, '.json');
    const response = await fetch(jsonFileName);


    const jsonStr = await response.text();
    const json: Array<ArchiveItemJson> = JSON.parse(jsonStr);
    //CHECK const json: Array<ArchiveItemJson> = await response.json();


    setArchiveItems(postProcessArchiveItemsJson(json));
  }) as () => void, []);
  if (archiveItems === null) {
    return <div id={pageCoreContent}>
      <h1 dangerouslySetInnerHTML={{__html: pageHeadingHtml}} />
      Downloading...
    </div>;
  } else {
    return <ActiveArchiveView archiveItems={archiveItems} itemsPerPage={itemsPerPage} />;
  }
}

type SetFilterValues = (filterData: FilterValues) => void;
interface ActiveArchiveViewProps {
  itemsPerPage: number;
  archiveItems: Array<ArchiveItem>;
}
function ActiveArchiveView({itemsPerPage, archiveItems}: ActiveArchiveViewProps) {
  const [filterValues, setFilterValues] = useState<FilterValues>(hashTofilterValues(location.hash));

  const setFilterValuesWrapped = (filterValues: FilterValues) => {
    window.location.href = '#' + filterValuesToHash(filterValues);
    setFilterValues(filterValues);
  };

  const filteredItems = filterArchiveItems(archiveItems, filterValues);
  const fieldSummaries = collectFieldSummaries(filteredItems);
  const pageCount = Math.ceil(filteredItems.length / itemsPerPage);
  if (filterValues.pageIndex >= pageCount) {
    filterValues.pageIndex = pageCount-1;
  }
  const pageHandling: NavigationPageHandling = {
    callback: (pageIndex: number) => {
      setFilterValuesWrapped({
        ...filterValues,
        pageIndex,
      });
    },
  };
  return <div id={pageCoreContent}>
    <h1>Archive</h1>
    <FilterView fieldSummaries={fieldSummaries} filterValues={filterValues} setFilterValues={setFilterValuesWrapped} />
    <br />
    <PageNavigation pageCount={pageCount} pageIndex={filterValues.pageIndex} pageHandling={pageHandling} />
    <ArchiveItemListView itemsPerPage={itemsPerPage} archiveItems={filteredItems} setFilterValues={setFilterValues} filterValues={filterValues} />
  </div>;
}

interface ArchiveItemListViewProps {
  itemsPerPage: number;
  archiveItems: Array<ArchiveItem>, setFilterValues: SetFilterValues, filterValues: FilterValues
}
function ArchiveItemListView({itemsPerPage, archiveItems, setFilterValues, filterValues}: ArchiveItemListViewProps) {
  const pageIndex = filterValues.pageIndex;
  return <div class="archive-item-list">
    {
      archiveItems.slice(pageIndex * itemsPerPage, (pageIndex+1) * itemsPerPage).map(
        archiveItem => <ArchiveItemView archiveItem={archiveItem} setFilterValues={setFilterValues} filterValues={filterValues} />
      )
    }
  </div>
}

function ArchiveItemView({archiveItem, setFilterValues, filterValues}: {archiveItem: ArchiveItem, setFilterValues: SetFilterValues, filterValues: FilterValues}) {
  return (
    <div class="archive-item">
      <div class="title">
        <SiteLink href={archiveItem.absWebPath}>
          <span dangerouslySetInnerHTML={{__html: plainToHtml(archiveItem.titlePlain)}} />
        </SiteLink>
      </div>
      <div>
        <span class="date">[{archiveItem.date}]</span> {
          archiveItem.tags.flatMap((tag, index) => {
            const tagLink = <a href="" onClick={handleTagClick.bind(null, tag)}>{tag}</a>;
            if (index === 0) {
              return [tagLink];
            } else {
              return [', ', tagLink];
            }
          })
        }
      </div>
    </div>
  );

  function handleTagClick(tag: string, event: MouseEvent) {
    event.preventDefault();
    // Remove the current tag from the constraints
    const filteredFieldConstraints = filterValues.fieldConstraints.filter(
      ({key,value}) => !(key === TAGS_FIELD_KEY && value === tag)
    );
    // Add the current tag before the filtered constraints
    const newFilterValues: FilterValues = {
      ...filterValues,
      pageIndex: 0,
      fieldConstraints: [
        {key: TAGS_FIELD_KEY, value: tag},
        ...filteredFieldConstraints,
      ],
    };
    setFilterValues(newFilterValues);
  }
}

function plainToHtml(plainText: string) {
  return plainText.replace(/`(.*?)`/g, '<code>$1</code>')
}

//========== Views: filtering

function FilterView({fieldSummaries, filterValues, setFilterValues}: {fieldSummaries: FieldSummaries, filterValues: FilterValues, setFilterValues: SetFilterValues}) {
  return (
    <div>
      <FilterTextView filterValues={filterValues} setFilterValues={setFilterValues} />
      <FieldSummariesView fieldSummaries={fieldSummaries} filterValues={filterValues} setFilterValues={setFilterValues} />
      <ConstraintsView filterValues={filterValues} setFilterValues={setFilterValues} />
    </div>
  );
}

function ConstraintsView({filterValues, setFilterValues}: {filterValues: FilterValues, setFilterValues: SetFilterValues}) {
  if (filterValues.fieldConstraints.length > 0) {
    return <div>
      <hr />
      {
        filterValues.fieldConstraints.map(
          ({key,value}) => <OneConstraintView constraintKey={key} constraintValue={value} filterValues={filterValues} setFilterValues={setFilterValues} />
        )
      }
    </div>;
  } else {
    return null;
  }
}

function OneConstraintView({constraintKey, constraintValue, filterValues, setFilterValues}: {constraintKey: string, constraintValue: string, filterValues: FilterValues, setFilterValues: SetFilterValues}) {
  return <div>
    <a href="" onClick={handleClick}>{fieldKeyToDisplayString.get(constraintKey)} = {constraintValue}</a>
  </div>;
  function handleClick(event: MouseEvent) {
    event.preventDefault();
    const newFilterValues: FilterValues = {
      ...filterValues,
      fieldConstraints: filterValues.fieldConstraints.filter(
        ({key, value}) => !(key === constraintKey && value === constraintValue)
      ),
    };
    setFilterValues(newFilterValues);
  }
}

function FilterTextView({filterValues, setFilterValues}: {filterValues: FilterValues, setFilterValues: SetFilterValues}) {
  return <input type="search" placeholder="Search titles and tags" value={filterValues.filterText ?? ''} onInput={handleInput} />;

  function handleInput(event: JSX.TargetedEvent<HTMLInputElement>) {
    setFilterValues({
      ...filterValues,
      pageIndex: 0,
      filterText: event.currentTarget.value,
    });
  }
}

function FieldSummariesView({fieldSummaries, filterValues, setFilterValues}: {fieldSummaries: FieldSummaries, filterValues: FilterValues, setFilterValues: SetFilterValues}) {
  return <div>
    {
      Object.entries(fieldSummaries).map(([fieldKey, fieldSummary]) => (
        <SummaryEntryView fieldKey={fieldKey} fieldSummary={fieldSummary} filterValues={filterValues} setFilterValues={setFilterValues} />
      ))
    }
  </div>;
}

function SummaryEntryView({fieldKey, fieldSummary, filterValues, setFilterValues}: {fieldKey: string, fieldSummary: Map<string, number>, filterValues: FilterValues, setFilterValues: SetFilterValues}) {
  const values = Array.from(
    iterFlatMap(
      toEntries(fieldSummary.entries()),
      ([index, [value, valueCount]]) => [
        ...(index === 0 ? [] : [', ']),
        <SummaryEntryValueView constraintKey={fieldKey} constraintValue={value} constraintValueCount={valueCount} filterValues={filterValues} setFilterValues={setFilterValues} />
      ]
    )
  );
  return (
    <div class="field-constraints-view">
      <b>{fieldKeyToDisplayString.get(fieldKey)}:</b> {
      values.length === 0 ? '—' : values
      }
    </div>
  );
}

function SummaryEntryValueView({constraintKey, constraintValue, constraintValueCount, filterValues, setFilterValues}: {constraintKey: string, constraintValue: string, constraintValueCount: number, filterValues: FilterValues, setFilterValues: SetFilterValues}) {
  return <a href="" onClick={handleClick}>{constraintValue}<sup>({constraintValueCount})</sup></a>;
  function handleClick(event: MouseEvent) {
    event.preventDefault();
    const newFilterValues: FilterValues = {
      ...filterValues,
      pageIndex: 0,
      fieldConstraints: [
        {key: constraintKey, value: constraintValue},
        ...filterValues.fieldConstraints,
      ],
    };
    setFilterValues(newFilterValues);
  }
}

//========== Data: filter values

interface FilterValues {
  pageIndex: number;
  filterText?: string;
  fieldConstraints: Array<FieldConstraint>;
}

interface FieldConstraint {
  key: string;
  value: string;
}

function filterArchiveItems(archiveItems: Array<ArchiveItem>, filterData: FilterValues): Array<ArchiveItem> {
  const filterTextRegex = filterData.filterText
    ? new RegExp(escapeForRegExp(filterData.filterText), 'i')
    : null
  ;
  return archiveItems.filter((archiveItem => {
    if (filterTextRegex
      && !filterTextRegex.test(archiveItem.titlePlain)
      && !archiveItem.tags.some((tag) => filterTextRegex.test(tag))
    ) {
      return false;
    }
    if (filterData.fieldConstraints.length > 0) {
      for (const {key: constraintKey, value: constraintValue} of filterData.fieldConstraints) {
        // constraintKey might not exist (if parsed from location hash)
        const fieldValue = (archiveItem as any)[constraintKey];
        if (typeof fieldValue !== undefined) {
          if (!satisfiesConstraint(fieldValue, constraintValue)) {
            return false;
          }
        }
      }
    }
    return true;
  }));
}

function satisfiesConstraint(value: string | Array<string>, constraint: string): boolean {
  if (Array.isArray(value)) {
    return value.includes(constraint);
  }
  return value === constraint;
}

// interface URLSearchParams2 extends URLSearchParams {
//   entries(): IterableIterator<[string, string]>;
// }

const TAGS_FIELD_KEY: FieldKey = 'tags';

const PAGE_FIELD_KEY: FieldKey = 'pageIndex';
const TEXT_FIELD_KEY: FieldKey = 'filterText';
const PAGE_DISPLAY_KEY = force(fieldKeyToDisplayString.get(PAGE_FIELD_KEY));
const TEXT_DISPLAY_KEY = force(fieldKeyToDisplayString.get(TEXT_FIELD_KEY));

function filterValuesToHash(filterValues: FilterValues): string {
  const params = new URLSearchParams();
  if (filterValues.filterText) {
    params.append(TEXT_DISPLAY_KEY, filterValues.filterText);
  }
  params.append(PAGE_DISPLAY_KEY, String(filterValues.pageIndex+1));
  for (const {key: fieldKey, value} of filterValues.fieldConstraints) {
    const displayKey = force(fieldKeyToDisplayString.get(fieldKey));
    params.append(displayKey, value);
  }
  return params.toString();
}

function hashTofilterValues(hash: string): FilterValues {
  hash = removePrefixMaybe(hash, '#');
  const params = new URLSearchParams(hash);
  
  const pageIndex = Number(params.get(PAGE_DISPLAY_KEY) ?? '1') - 1;
  const filterValues: FilterValues = {
    pageIndex,
    fieldConstraints: [],
  };
  const text = params.get(TEXT_DISPLAY_KEY);
  if (text) {
    filterValues.filterText = text;
  }
  params.delete(PAGE_DISPLAY_KEY);
  params.delete(TEXT_DISPLAY_KEY);
  for (const [displayKey, value] of params.entries()) {
    const fieldKey = displayStringToFieldKey.get(displayKey);
    if (fieldKey) {
      filterValues.fieldConstraints.push({key: fieldKey, value});
    }
  }

  return filterValues;
}

//========== Data: field summaries

interface FieldSummaries {
  year: Map<string, number>;
  month: Map<string, number>;
  tags: Map<string, number>;
}
function collectFieldSummaries(archiveItems: Array<ArchiveItem>): FieldSummaries {
  const summaries: FieldSummaries = {
    year: new Map(),
    month: new Map(),
    tags: new Map(),
  };
  for (const archiveItem of archiveItems) {
    incCounter(summaries.year, archiveItem.year);
    incCounter(summaries.month, archiveItem.month);
    for (const tag of archiveItem.tags) {
      incCounter(summaries.tags, tag);
    }
  }
  // If a facet appears in every archiveItem, it can’t be used to filter anymore
  // and should not be shown.
  const fullCount = archiveItems.length;
  return {
    year: sortYear(removeEntriesWithCount(summaries.year, fullCount)),
    month: sortMonth(removeEntriesWithCount(summaries.month, fullCount)),
    tags: sortTags(removeEntriesWithCount(summaries.tags, fullCount)),
  };
}

function removeEntriesWithCount(entries: Map<string, number>, fullCount: number): Map<string, number> {
  return new Map(
    iterFilter(
      entries,
      ([_value, count]) => count !== fullCount
    )
  );
}

function incCounter(map: Map<string, number>, key: string) {
  const counter = map.get(key) ?? 0;
  map.set(key, counter+1);
}

function sortMonth(month: Map<string, number>) {
  const entries = Array.from(month.entries());
  // Ascending: lower months come first
  entries.sort(
    ([month1, _count1], [month2, _count2]) => Number(month1) - Number(month2)
  );
  return new Map(entries);
}

function sortYear(year: Map<string, number>) {
  const entries = Array.from(year.entries());
  // Ascending: higher years come first
  entries.sort(
    ([year1, _count1], [year2, _count2]) => Number(year2) - Number(year1)
  );
  return new Map(entries);
}

function sortTags(tags: Map<string, number>) {
  const entries = Array.from(tags.entries());
  // Descending: higher counts come first
  entries.sort(
    ([_tag1, count1], [_tag2, count2]) => count2 - count1
  );
  return new Map(entries);
}

//========== Data: archive items

interface ArchiveItem extends ArchiveItemJson {
  year: string,
  month: string,
}

function postProcessArchiveItemsJson(archiveItemsJson: Array<ArchiveItemJson>): Array<ArchiveItem> {
  return archiveItemsJson.map(
    (archiveItemJson) => {
      return {
        ...archiveItemJson,
        year: archiveItemJson.date.slice(0, 4),
        month: archiveItemJson.date.slice(5, 7),
      };
    }
  );
}

//========== Helpers

function force(value: undefined | string): string {
  if (value === undefined) {
    throw new Error();
  }
  return value;
}
